/*
  Outdoor Living header behaviour.
  - The bars fold as the page scrolls: --ol-fold on .ol-hdr-root runs from 0 at the top to 1 once the page has
    scrolled 120px, and data-ol-compact is set once fully folded.
  - The search prompt types "I'm looking for" once. Then each product type types in letter by
    letter, holds, is deleted letter by letter, and the next one follows. The timing runs from
    animation frames, so the pace stays even. Reduced motion turns off the caret blink only;
    the typing stays, because it changes words and does not move anything.
  - Suggestions update as the customer types. The previous results stay on screen, dimmed,
    until the new ones arrive. Requests are cancelled when superseded and responses are cached.
    Matched text is highlighted using the match the store returns.
  Markup: sections/header.liquid and snippets/ol-header-search.liquid.
  Styles: assets/ol-header.css.
*/
(function () {
  'use strict';

  var COMPACT_AFTER = 120;
  var SUGGEST_DELAY = 200;
  var CACHE_LIMIT = 30;
  var LEAD_STEP = 60;
  var TYPE_STEP = 85;
  var ERASE_STEP = 50;
  var HOLD = 3000;
  var GAP = 350;

  var mobile = window.matchMedia('(max-width: 767px)');

  function element(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  }

  /* Appends a store suggestion that may carry <mark>…</mark> around the typed part. <mark> becomes a
     highlight; any other tag (Shopify wraps the completion in <span>) is dropped, so no tag text shows. */
  function decodeEntities(text) {
    var area = document.createElement('textarea');
    area.innerHTML = text;
    return area.value;
  }

  function appendMarked(parent, markup) {
    var marked = false;
    String(markup).split(/(<\/?[a-z][^>]*>)/i).forEach(function (part) {
      if (!part) return;
      if (/^<\/?[a-z]/i.test(part)) {
        if (/^<mark[\s>]/i.test(part)) marked = true;
        else if (/^<\/mark>/i.test(part)) marked = false;
        return;
      }
      var text = decodeEntities(part);
      parent.appendChild(marked ? element('mark', '', text) : document.createTextNode(text));
    });
  }

  function formatPrice(value, currency) {
    var amount = parseFloat(value);
    if (isNaN(amount)) return '';
    try {
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: currency || 'GBP',
        minimumFractionDigits: amount % 1 ? 2 : 0,
        maximumFractionDigits: 2
      }).format(amount);
    } catch (error) {
      return '';
    }
  }

  function parseTerms(raw) {
    try {
      var list = JSON.parse(raw || '[]');
      if (!Array.isArray(list)) return [];
      return list
        .map(function (text) { return String(text).trim(); })
        .filter(Boolean);
    } catch (error) {
      return [];
    }
  }

  /* Words in lower case, with a trailing "s" removed, so "Hot Tubs" and "hot tub" compare equal */
  function words(text) {
    return String(text)
      .toLowerCase()
      .replace(/[^a-z0-9£ ]+/g, ' ')
      .split(/\s+/)
      .filter(Boolean)
      .map(function (word) { return word.length > 3 && /s$/.test(word) ? word.slice(0, -1) : word; })
      .join(' ');
  }

  /* 0 = the title is the search, 1 = the title starts with it, 2 = it appears inside, 3 = other */
  function matchRank(title, query) {
    var wanted = words(query);
    var found = words(title);
    if (found === wanted) return 0;
    if (found.indexOf(wanted) === 0) return 1;
    if (found.indexOf(wanted) > -1) return 2;
    return 3;
  }

  function trackEvent(name, data) {
    var analytics = window.Shopify && window.Shopify.analytics;
    if (analytics && typeof analytics.publish === 'function') {
      analytics.publish(name, data);
    }
  }

  /* The fold follows the scroll position: 0 at the top of the page, 1 once the page has scrolled COMPACT_AFTER pixels.
     Every fold size in the header is worked out from --ol-fold, so the bar moves with the scroll. */
  function setFold(header) {
    var y = window.pageYOffset || document.documentElement.scrollTop || 0;
    var fold = Math.min(1, Math.max(0, y / COMPACT_AFTER));
    header.style.setProperty('--ol-fold', fold.toFixed(3));
    /* Set only once fully folded, so the parts that hide (and leave the tab order) do so when they are invisible */
    header.setAttribute('data-ol-compact', fold >= 0.99 ? 'true' : 'false');
    followSpace(header);
  }

  /* The page space follows whichever bar is showing (desktop or phone) as it folds, so no gap is left below it.
     keepSpace measures the expanded bar first; this then sets the space to the folded bar. */
  function followSpace(header) {
    var space = header.querySelector('[data-ol-space]');
    if (!space) return;
    /* While the phone search is open it takes its row out of the bar, so leave the space alone until it closes */
    if (header.querySelector('.ol-hsearch--mobile[data-ol-open]')) return;
    var bars = header.querySelectorAll('[data-ol-bar]');
    var shown = null;
    for (var i = 0; i < bars.length; i++) {
      if (window.getComputedStyle(bars[i]).display !== 'none') shown = bars[i];
    }
    if (!shown) return;
    space.style.height = Math.max(0, shown.offsetHeight - 1) + 'px';
  }

  function initCompact(header) {
    if (header.getAttribute('data-ol-ready') === 'true') return;
    header.setAttribute('data-ol-ready', 'true');

    var frame = 0;

    function update() {
      frame = 0;
      setFold(header);
    }

    window.addEventListener('scroll', function () {
      if (!frame) frame = window.requestAnimationFrame(update);
    }, { passive: true });

    setFold(header);
  }

  /* The space is measured with the bar expanded and transitions switched off, then the fold is put back to the scroll
     position, which sets the space to the folded bar (followSpace). */
  function keepSpace(header) {
    var space = header.querySelector('[data-ol-space]');
    if (!space) return;
    var bars = header.querySelectorAll('[data-ol-bar]');
    var shown = null;
    header.classList.add('ol-hdr-still');
    header.style.setProperty('--ol-fold', '0');
    for (var i = 0; i < bars.length; i++) {
      if (window.getComputedStyle(bars[i]).display !== 'none') shown = bars[i];
    }
    /* One pixel less than the bar: the bar's own 1px line covers the strip's top line below it */
    space.style.height = Math.max(0, (shown ? shown.offsetHeight : 0) - 1) + 'px';
    setFold(header);
    header.offsetHeight;
    header.classList.remove('ol-hdr-still');
  }

  function initSearch(root) {
    if (root.getAttribute('data-ol-ready') === 'true') return;
    root.setAttribute('data-ol-ready', 'true');

    var input = root.querySelector('[data-ol-input]');
    var typed = root.querySelector('[data-ol-typed]');
    var lead = root.querySelector('[data-ol-lead]');
    var term = root.querySelector('[data-ol-term]');
    var clear = root.querySelector('[data-ol-clear]');
    var close = root.querySelector('[data-ol-close]');
    var panel = root.querySelector('[data-ol-panel]');
    var results = root.querySelector('[data-ol-results]');
    var status = root.querySelector('[data-ol-status]');
    var allLink = root.querySelector('[data-ol-all]');

    var types = root.getAttribute('data-ol-types') || '';
    var currency = root.getAttribute('data-ol-currency') || 'GBP';
    var suggestUrl = root.getAttribute('data-ol-suggest') || '';
    var searchUrl = root.getAttribute('data-ol-search-url') || '/search';
    var terms = parseTerms(root.getAttribute('data-ol-terms'));
    var leadText = lead ? lead.textContent.trim() : '';

    var prompt = { running: false, leadDone: false, frame: 0, index: 0 };
    var timeline = { phase: 'lead', start: 0, lead: -1, text: null };
    var suggestTimer = 0;
    var controller = null;
    var searchId = null;
    var optionCount = 0;
    var activeIndex = -1;
    var cache = {};
    var cacheKeys = [];

    function renderLead(count) {
      if (!lead || count === timeline.lead) return;
      timeline.lead = count;
      lead.textContent = leadText.slice(0, count);
    }

    function renderTerm(text) {
      if (!term || text === timeline.text) return;
      timeline.text = text;
      term.textContent = text;
    }

    function phaseLength(phase, word) {
      if (phase === 'lead') return leadText.length * LEAD_STEP;
      if (phase === 'type') return word.length * TYPE_STEP;
      if (phase === 'hold') return HOLD;
      if (phase === 'erase') return word.length * ERASE_STEP;
      return GAP;
    }

    /* Moves the timeline on from the phase that has just ended */
    function advancePhase() {
      if (timeline.phase === 'lead') {
        /* A slow frame can jump past the last letters, so the lead is always finished here */
        renderLead(leadText.length);
        prompt.leadDone = true;
        timeline.phase = 'type';
      } else if (timeline.phase === 'type') {
        timeline.phase = 'hold';
      } else if (timeline.phase === 'hold') {
        timeline.phase = 'erase';
      } else if (timeline.phase === 'erase') {
        prompt.index = (prompt.index + 1) % terms.length;
        timeline.phase = 'gap';
      } else {
        timeline.phase = 'type';
      }
    }

    /* One frame: show the letters the current phase has reached. A long gap between frames (a hidden tab, a busy page)
       can cover several phases. They are all passed in this frame, so the prompt shows where it should be now rather
       than replaying the missed phases one frame at a time, which looked like the product type was racing. */
    function step(now) {
      if (!prompt.running) return;

      var word = terms[prompt.index] || '';
      var elapsed = now - timeline.start;
      var length = phaseLength(timeline.phase, word);
      var passed = 0;

      while (elapsed >= length && passed < 100000) {
        timeline.start += length;
        elapsed -= length;
        advancePhase();
        passed += 1;
        word = terms[prompt.index] || '';
        length = phaseLength(timeline.phase, word);
      }

      if (timeline.phase === 'lead') {
        renderLead(Math.min(leadText.length, Math.floor(elapsed / LEAD_STEP) + 1));
      } else if (timeline.phase === 'type') {
        renderTerm(word.slice(0, Math.min(word.length, Math.floor(elapsed / TYPE_STEP) + 1)));
      } else if (timeline.phase === 'hold') {
        renderTerm(word);
      } else if (timeline.phase === 'erase') {
        renderTerm(word.slice(0, Math.max(0, word.length - Math.floor(elapsed / ERASE_STEP))));
      } else {
        renderTerm('');
      }

      prompt.frame = window.requestAnimationFrame(step);
    }

    function startPrompt() {
      if (prompt.running || !term || !terms.length) return;
      prompt.running = true;
      timeline.start = window.performance.now();
      timeline.lead = -1;
      timeline.text = null;
      timeline.phase = prompt.leadDone ? 'type' : 'lead';
      if (!prompt.leadDone) renderTerm('');
      prompt.frame = window.requestAnimationFrame(step);
    }

    function stopPrompt() {
      prompt.running = false;
      if (prompt.frame) window.cancelAnimationFrame(prompt.frame);
      prompt.frame = 0;
    }

    /* The prompt shows whenever the field is empty, focused or not, so an open field is never blank */
    /* The prompt steps aside as soon as the customer clicks into the field, and comes back once the field is empty
       again (after they clear it, or leave the field). The same on desktop and on the phone. */
    var promptCleared = false;

    function syncPrompt() {
      var inField = document.activeElement === input && !promptCleared;
      var hidden = input.value !== '' || inField;
      typed.classList.toggle('is-hidden', hidden);
      if (hidden) {
        stopPrompt();
      } else {
        startPrompt();
      }
    }

    /* Search state */
    function open() {
      if (root.hasAttribute('data-ol-open')) return;
      root.setAttribute('data-ol-open', '');
      if (mobile.matches) document.body.style.overflow = 'hidden';
    }

    function closeSearch() {
      root.removeAttribute('data-ol-open');
      showPanel(false);
      input.removeAttribute('aria-activedescendant');
      activeIndex = -1;
      if (mobile.matches) document.body.style.overflow = '';
      /* Refit the page space to the bar now that its search row is back */
      var header = root.closest('[data-ol-header]');
      if (header) setFold(header);
    }

    function showPanel(show) {
      panel.hidden = !show;
      input.setAttribute('aria-expanded', show ? 'true' : 'false');
    }

    function setAllLink(query) {
      if (query) {
        allLink.href = searchUrl + '?q=' + encodeURIComponent(query);
        allLink.textContent = 'See all results for “' + query + '”';
      } else {
        allLink.href = searchUrl;
        allLink.textContent = 'Search the whole store';
      }
    }

    function abortRequest() {
      window.clearTimeout(suggestTimer);
      if (controller) {
        controller.abort();
        controller = null;
      }
    }

    var emptyTemplate = root.querySelector('[data-ol-empty]');

    /* Phone, search open and nothing typed yet: one line that says what happens next. Desktop shows no dropdown. */
    function renderEmpty() {
      results.textContent = '';
      status.textContent = '';
      setAllLink('');
      activeIndex = -1;
      input.removeAttribute('aria-activedescendant');
      if (!emptyTemplate) {
        showPanel(false);
        return;
      }
      results.appendChild(emptyTemplate.content.cloneNode(true));
      showPanel(true);
    }

    /* Nothing typed yet, or too short to suggest: on a phone the open search shows its hint, on desktop no dropdown */
    function reset() {
      abortRequest();
      results.classList.remove('is-loading');
      if (mobile.matches && root.hasAttribute('data-ol-open')) {
        renderEmpty();
        return;
      }
      results.textContent = '';
      status.textContent = '';
      setAllLink('');
      activeIndex = -1;
      input.removeAttribute('aria-activedescendant');
      showPanel(false);
    }

    function remember(query, data) {
      if (!cache.hasOwnProperty(query)) cacheKeys.push(query);
      cache[query] = data;
      if (cacheKeys.length > CACHE_LIMIT) delete cache[cacheKeys.shift()];
    }

    /* Suggestions for what the customer has typed. Earlier results stay on screen, dimmed, until these arrive. */
    function suggest(query) {
      setAllLink(query);

      if (cache.hasOwnProperty(query)) {
        renderResults(cache[query], query);
        return;
      }

      if (!types || !suggestUrl) {
        results.classList.remove('is-loading');
        results.textContent = '';
        status.textContent = 'Suggestions are not available right now. Press Enter to search.';
        showPanel(true);
        return;
      }

      if (controller) controller.abort();
      controller = typeof AbortController === 'function' ? new AbortController() : null;

      /* limit_scope=each: up to ten of each type, so the store's main collections are not crowded out */
      var url = suggestUrl +
        '?q=' + encodeURIComponent(query) +
        '&resources[type]=' + types +
        '&resources[limit]=10' +
        '&resources[limit_scope]=each' +
        '&resources[options][unavailable_products]=hide';

      if (results.children.length) {
        results.classList.add('is-loading');
        status.textContent = '';
      } else {
        status.textContent = 'Searching…';
      }
      showPanel(true);

      fetch(url, {
        headers: { Accept: 'application/json' },
        signal: controller ? controller.signal : undefined
      })
        .then(function (response) {
          if (!response.ok) throw new Error('Suggestions request failed: ' + response.status);
          return response.json();
        })
        .then(function (data) {
          remember(query, data);
          if (input.value.trim().replace(/\s+/g, ' ') !== query) return;
          renderResults(data, query);
        })
        .catch(function (error) {
          if (error && error.name === 'AbortError') return;
          results.classList.remove('is-loading');
          results.textContent = '';
          status.textContent = 'Suggestions are not available right now. Press Enter to search.';
          showPanel(true);
        });
    }

    function buildOption(item, kind) {
      optionCount += 1;
      var link = element('a', 'ol-hsearch__option ol-hsearch__option--' + kind);
      link.id = input.id + '-option-' + optionCount;
      link.href = item.url || searchUrl;
      link.setAttribute('role', 'option');
      link.setAttribute('aria-selected', 'false');
      link.tabIndex = -1;

      /* Only real images are shown: no empty placeholder boxes. Results appear in a few rows at the top of the page, so the
         images load straight away and the tint behind them shows only for the moment a photo takes to arrive. */
      if (kind !== 'query' && item.image) {
        var media = element('span', 'ol-hsearch__option-media');
        var image = element('img');
        image.src = item.image;
        image.alt = '';
        image.width = 480;
        image.height = 360;
        image.decoding = 'async';
        image.loading = 'eager';
        media.appendChild(image);
        link.appendChild(media);
      }

      var body = element('span', 'ol-hsearch__option-body');
      var title = element('span', 'ol-hsearch__option-title');
      if (kind === 'query' && item.styled_text) {
        appendMarked(title, item.styled_text);
      } else {
        title.textContent = item.title || item.text || '';
      }
      body.appendChild(title);
      if (kind === 'product' && item.vendor) {
        body.appendChild(element('span', 'ol-hsearch__option-sub', item.vendor));
      }
      link.appendChild(body);

      if (kind === 'product') {
        var price = formatPrice(item.price, currency);
        if (price) link.appendChild(element('span', 'ol-hsearch__option-price', price));
      }

      link.addEventListener('click', function () {
        if (kind === 'product' && searchId) {
          trackEvent('search_result_clicked', { searchResultId: searchId, productId: item.id });
        }
      });

      return link;
    }

    function renderResults(data, query) {
      var found = (data && data.resources && data.resources.results) || {};
      /* Collections whose title matches the search most closely come first; the order is otherwise kept */
      var collections = (found.collections || []).slice().sort(function (a, b) {
        return matchRank(a.title, query) - matchRank(b.title, query);
      });
      var groups = [
        { title: 'Suggested searches', kind: 'query', max: 3, items: found.queries || [] },
        { title: 'Collections', kind: 'collection', max: 4, items: collections },
        { title: 'Products', kind: 'product', max: 4, items: found.products || [] }
      ];
      var shown = 0;

      results.classList.remove('is-loading');
      results.textContent = '';
      groups.forEach(function (group) {
        var items = group.items.slice(0, group.max);
        if (!items.length) return;
        shown += items.length;

        var section = element('div', 'ol-hsearch__group');
        var list = element('div', 'ol-hsearch__list ol-hsearch__list--' + group.kind);
        section.appendChild(element('p', 'ol-hsearch__heading', group.title));
        list.setAttribute('role', 'listbox');
        list.setAttribute('aria-label', group.title);
        items.forEach(function (item) {
          list.appendChild(buildOption(item, group.kind));
        });
        section.appendChild(list);
        results.appendChild(section);
      });

      searchId = (data && data.search_id) || null;
      status.textContent = shown ? '' : 'No matches for “' + query + '”. Try a shorter word, or search the whole store.';
      setAllLink(query);
      activeIndex = -1;
      input.removeAttribute('aria-activedescendant');
      showPanel(true);

      if (searchId) trackEvent('search_submitted', { searchResultId: searchId, query: query });
    }

    /* Keyboard: arrows move through suggestions, Enter opens the highlighted one, Esc closes */
    function options() {
      return Array.prototype.slice.call(results.querySelectorAll('[role="option"]'));
    }

    function setActive(list) {
      list.forEach(function (option, index) {
        option.setAttribute('aria-selected', index === activeIndex ? 'true' : 'false');
      });
      if (activeIndex > -1 && list[activeIndex]) {
        input.setAttribute('aria-activedescendant', list[activeIndex].id);
        list[activeIndex].scrollIntoView({ block: 'nearest' });
      } else {
        input.removeAttribute('aria-activedescendant');
      }
    }

    input.addEventListener('keydown', function (event) {
      var list = options();

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        if (!list.length) return;
        event.preventDefault();
        if (event.key === 'ArrowDown') {
          activeIndex = (activeIndex + 1) % list.length;
        } else {
          activeIndex = activeIndex <= 0 ? list.length - 1 : activeIndex - 1;
        }
        setActive(list);
        return;
      }

      if (event.key === 'Enter' && activeIndex > -1 && list[activeIndex]) {
        event.preventDefault();
        window.location.href = list[activeIndex].href;
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        closeSearch();
        input.blur();
      }
    });

    input.addEventListener('focus', function () {
      open();
      syncPrompt();
      var query = input.value.trim().replace(/\s+/g, ' ');
      if (query.length >= 2) suggest(query);
      else reset();
    });

    input.addEventListener('blur', function () {
      promptCleared = false;
      syncPrompt();
    });

    input.addEventListener('input', function () {
      var query = input.value.trim().replace(/\s+/g, ' ');
      clear.hidden = input.value === '';
      if (input.value === '') promptCleared = true;
      syncPrompt();
      open();

      if (query.length < 2) {
        reset();
        return;
      }
      abortRequest();
      suggestTimer = window.setTimeout(function () { suggest(query); }, SUGGEST_DELAY);
    });

    /* Clearing empties the field and nothing more. Focusing the field opens the phone sheet, so on a phone the field is
       only focused when the sheet is already open (the customer is typing there). On desktop the field keeps focus as before. */
    clear.addEventListener('click', function () {
      input.value = '';
      clear.hidden = true;
      promptCleared = true;
      reset();
      syncPrompt();
      if (!mobile.matches || root.hasAttribute('data-ol-open')) input.focus();
    });

    /* Searching for nothing does nothing: an empty or blank field keeps the customer on the page, with no navigation.
       This covers the search button and Enter in the field. */
    var form = root.querySelector('.ol-hsearch__form');
    if (form) {
      form.addEventListener('submit', function (event) {
        if (input.value.trim() === '') event.preventDefault();
      });
    }

    if (close) {
      close.addEventListener('click', function () {
        closeSearch();
        input.blur();
      });
    }

    document.addEventListener('click', function (event) {
      if (!root.hasAttribute('data-ol-open') || mobile.matches) return;
      if (root.contains(event.target)) return;
      closeSearch();
      input.blur();
    });

    setAllLink('');
    showPanel(false);
    syncPrompt();
  }

  function fitSpace() {
    document.querySelectorAll('[data-ol-header]').forEach(keepSpace);
  }

  var fitBound = false;
  var fitFrame = 0;

  function init() {
    document.querySelectorAll('[data-ol-header]').forEach(initCompact);
    document.querySelectorAll('[data-ol-search]').forEach(initSearch);
    fitSpace();
    if (fitBound) return;
    fitBound = true;
    /* Fonts and widths change the bar's height; the space follows, once per frame at most */
    var refit = function () {
      if (!fitFrame) fitFrame = window.requestAnimationFrame(function () { fitFrame = 0; fitSpace(); });
    };
    window.addEventListener('resize', refit, { passive: true });
    window.addEventListener('load', refit);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(refit);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();

/* Header basket count: keeps the badge right on every page.
   The slide-out basket changes the basket with /cart/change.js etc. but its own counter
   points at an old element, so after any basket change we re-read /cart.js and update the badge
   (hidden when the basket is empty). Also refreshes when a page is shown again from the back button. */
(function () {
  if (window.__olHdrCount) return;
  window.__olHdrCount = true;
  var re = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var nativeFetch = window.fetch ? window.fetch.bind(window) : null;
  var timer;
  function paint(n) {
    var els = document.querySelectorAll('.ol-hdr-badge.js-cart-count');
    for (var i = 0; i < els.length; i++) {
      els[i].textContent = n;
      if (n > 0) { els[i].classList.remove('jbf-hidden'); } else { els[i].classList.add('jbf-hidden'); }
    }
  }
  function refresh() {
    if (!nativeFetch) return;
    clearTimeout(timer);
    timer = setTimeout(function () {
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      nativeFetch(root + 'cart.js', { credentials: 'same-origin', cache: 'no-store' })
        .then(function (r) { return r.json(); })
        .then(function (c) { if (c && typeof c.item_count === 'number') paint(c.item_count); })
        .catch(function () {});
    }, 250);
  }
  window.olRefreshHeaderCount = refresh;

  var origOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    try {
      if (re.test(String(url))) { this.addEventListener('loadend', refresh); }
    } catch (e) {}
    return origOpen.apply(this, arguments);
  };

  if (nativeFetch) {
    var wrapped = window.fetch;
    window.fetch = function (input) {
      var p = wrapped.apply(this, arguments);
      try {
        var u = typeof input === 'string' ? input : (input && input.url) || '';
        if (re.test(String(u))) { p.then(refresh, refresh); }
      } catch (e) {}
      return p;
    };
  }

  window.addEventListener('pageshow', function (e) { if (e.persisted) refresh(); });
})();
